import React, { useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import StyledContainer from '../../components/UI/atoms/StyledContainer';
import Text from '../../components/UI/atoms/Text';
import Card from '../../components/UI/molecules/Card';
import Button from '../../components/UI/atoms/Button';
import AdminNav from './components/AdminNav';
import { authPost, authGet } from '../../services/authCalls';
import { useApp } from '../../AppContext';

const REPORTS = [
  {
    key: 'subs',
    title: 'Subscription Report — All Firms',
    description: 'Downloads a CSV of all subscriptions across all firms with plan, status, renewal and firm details.',
    url: '/api/admin/report/subscription-all-firms',
    method: 'post',
    body: 1,
    action: 'Download CSV',
  },
  {
    key: 'free',
    title: 'Free User List',
    description: 'Downloads a CSV of all free (non-paying) users in the system.',
    url: '/api/admin/report/free-user-list',
    method: 'post',
    body: '',
    action: 'Download CSV',
  },
  {
    key: 'shipping',
    title: 'All Users Shipping Details',
    description: 'Downloads a CSV of all users with their shipping address, copies, subscription type and contact info.',
    url: '/api/admin/report/shipping-details',
    method: 'get',
    action: 'Download CSV',
  },
  {
    key: 'active-subs',
    title: 'All Active Subscriptions',
    description: 'Downloads a CSV of all currently active subscriptions with plan name, product, renewal period and firm details.',
    url: '/api/admin/report/active-subscriptions',
    method: 'get',
    action: 'Download CSV',
  },
];

const toCSV = (rows) => {
  if (!rows || rows.length === 0) return '';
  const headers = Object.keys(rows[0]);
  const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return [headers.join(','), ...rows.map(r => headers.map(h => escape(r[h])).join(','))].join('\n');
};

const downloadCSV = (data, filename) => {
  const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export default function Reports() {
  const { handleMessage } = useApp();
  const [loadingKey, setLoadingKey] = useState('');
  const [lastRan, setLastRan] = useState({});

  const run = (r) => {
    setLoadingKey(r.key);
    const req = r.method === 'get' ? authGet(r.url) : authPost(r.url, r.body);
    req
      .then(res => {
        setLastRan(prev => ({ ...prev, [r.key]: new Date().toLocaleTimeString() }));
        if (res?.data) {
          const rows = Array.isArray(res.data) ? res.data : [res.data];
          if (rows.length > 0) {
            downloadCSV(toCSV(rows), `${r.key}-${Date.now()}.csv`);
            handleMessage('success', 'CSV downloaded');
          } else {
            handleMessage('success', 'No data returned');
          }
        } else {
          handleMessage('success', 'Report triggered — results will be emailed shortly');
        }
      })
      .catch(err => {
        const msg = err?.response?.data || err?.response?.headers?.errormessage || 'Unable to run report';
        handleMessage('error', msg);
      })
      .finally(() => setLoadingKey(''));
  };

  return (
    <StyledContainer sx={{ maxWidth: '900px', py: '32px' }}>
      <AdminNav />
      <Text type='h1' sx={{ mb: '20px' }}>Reports</Text>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {REPORTS.map(r => (
          <Card key={r.key} sx={{ margin: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1 }}>
                <Text sx={{ fontWeight: 700, mb: '4px' }}>{r.title}</Text>
                <Text sx={{ fontSize: '13px', color: 'text.secondary' }}>{r.description}</Text>
                {lastRan[r.key] && (
                  <Text sx={{ fontSize: '12px', color: 'success.main', mt: '6px' }}>
                    ✓ Last triggered at {lastRan[r.key]}
                  </Text>
                )}
              </Box>
              <Box sx={{ minWidth: '120px', pt: '2px' }}>
                <Button
                  type='outlined'
                  disabled={!!loadingKey}
                  onClick={() => run(r)}
                  sx={{ width: '100%' }}
                >
                  {loadingKey === r.key
                    ? <><CircularProgress size={13} sx={{ mr: '6px' }} />Running…</>
                    : r.action
                  }
                </Button>
              </Box>
            </Box>
          </Card>
        ))}
      </Box>
    </StyledContainer>
  );
}
